(function () {
  'use strict';

  /*
   * Evento personalizzato usato come trigger in GTM.
   */
  const EVENT_NAME = 'add_to_wishlist_stape';

  /*
   * Componenti Wishlist King:
   *
   * wishlist-button-collection = cuore nelle collezioni
   * wishlist-button-block      = pulsante nella pagina prodotto
   */
  const COMPONENT_SELECTOR =
    'wishlist-button-collection, wishlist-button-block';

  const BUTTON_SELECTOR =
    'wishlist-button-collection button.wk-button, ' +
    'wishlist-button-block button.wk-button';

  /*
   * Evita eventi duplicati mentre il prodotto
   * viene elaborato.
   */
  const requestsInProgress = new Set();

  /**
   * Verifica che Wishlist King abbia realmente
   * aggiunto il prodotto.
   *
   * Dopo l'aggiunta il pulsante riceve:
   *
   * class="... wk-selected"
   */
  function waitForWishlistSelection(component, timeout = 3000) {
    return new Promise(function (resolve) {
      function isSelected() {
        return Boolean(
          component.querySelector('button.wk-button.wk-selected')
        );
      }

      if (isSelected()) {
        resolve(true);
        return;
      }

      let timeoutId;

      const observer = new MutationObserver(function () {
        if (!isSelected()) {
          return;
        }

        clearTimeout(timeoutId);
        observer.disconnect();
        resolve(true);
      });

      observer.observe(component, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-label']
      });

      timeoutId = setTimeout(function () {
        observer.disconnect();
        resolve(false);
      }, timeout);
    });
  }

  /**
   * Cerca l'ID della variante nei dati disponibili
   * all'interno di Wishlist King.
   */
  function getVariantId(component) {
    const possibleVariantIds = [];

    /*
     * Presente normalmente nella pagina prodotto:
     *
     * data-variant-id="47514125107520"
     */
    possibleVariantIds.push(
      component.dataset.variantId
    );

    /*
     * Proprietà interne di Wishlist King.
     * Sono particolarmente utili nelle collezioni.
     */
    try {
      possibleVariantIds.push(
        component.wishlistItem?.variantId,
        component.wishlistItem?.variant_id,
        component.productInfo?.variantId,
        component.productInfo?.variant_id,
        component.productInfo?.selectedVariant?.id,
        component.productInfo?.selected_variant?.id
      );
    } catch (error) {
      /*
       * Le proprietà interne possono non essere
       * disponibili durante il caricamento.
       */
    }

    /*
     * Controlla anche il parametro variante nell'URL:
     *
     * ?variant=47514125107520
     */
    try {
      const urlVariantId =
        new URL(window.location.href)
          .searchParams
          .get('variant');

      possibleVariantIds.push(urlVariantId);
    } catch (error) {
      // URL non leggibile.
    }

    for (const possibleVariantId of possibleVariantIds) {
      const numericVariantId = Number(possibleVariantId);

      if (
        Number.isFinite(numericVariantId) &&
        numericVariantId > 0
      ) {
        return numericVariantId;
      }
    }

    return null;
  }

  /**
   * Recupera i dati del prodotto tramite
   * l'endpoint Ajax di Shopify.
   */
  async function fetchProduct(productHandle) {
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const requestUrl =
      shopRoot +
      'products/' +
      encodeURIComponent(productHandle) +
      '.js';

    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        'Prodotto non recuperato. HTTP ' +
        response.status
      );
    }

    return response.json();
  }

  /**
   * Seleziona la variante corretta.
   *
   * Se l'ID non è disponibile, usa la prima
   * variante acquistabile.
   */
  function selectVariant(product, requestedVariantId) {
    if (!Array.isArray(product.variants)) {
      return null;
    }

    if (requestedVariantId) {
      const requestedVariant =
        product.variants.find(function (variant) {
          return (
            Number(variant.id) ===
            Number(requestedVariantId)
          );
        });

      if (requestedVariant) {
        return requestedVariant;
      }
    }

    const firstAvailableVariant =
      product.variants.find(function (variant) {
        return variant.available;
      });

    return (
      firstAvailableVariant ||
      product.variants[0] ||
      null
    );
  }

  /**
   * Recupera la valuta attiva.
   */
  function getCurrency() {
    return (
      window.customDataLayerConfig?.currency ||
      window.Shopify?.currency?.active ||
      'EUR'
    );
  }

  /**
   * Genera l'evento nel dataLayer.
   */
  function pushWishlistEvent(product, variant) {
    window.dataLayer = window.dataLayer || [];

    const currency = getCurrency();

    const productId = String(product.id);
    const variantId = String(variant.id);

    /*
     * Formato richiesto:
     *
     * PRODUCT_ID_VARIANT_ID
     *
     * Esempio:
     * 9104422404416_47997377937728
     */
    const formattedItemId =
      productId + '_' + variantId;

    /*
     * Shopify restituisce normalmente i prezzi
     * in centesimi.
     */
    const price = Number(
      (
        Number(variant.price || 0) / 100
      ).toFixed(2)
    );

    const compareAtPrice = Number(
      (
        Number(variant.compare_at_price || 0) / 100
      ).toFixed(2)
    );

    const discount =
      compareAtPrice > price
        ? Number(
            (compareAtPrice - price).toFixed(2)
          )
        : 0;

    const item = {
      index: 0,

      /*
       * Senza shopify_IT_.
       */
      item_id: formattedItemId,
      id: formattedItemId,

      product_id: productId,
      variant_id: variantId,

      item_name: product.title,
      quantity: 1,
      price: price,
      discount: discount
    };

    const productType =
      product.type ||
      product.product_type;

    if (productType) {
      item.item_category = productType;
    }

    if (product.vendor) {
      item.item_brand = product.vendor;
    }

    if (
      variant.title &&
      variant.title !== 'Default Title'
    ) {
      item.item_variant = variant.title;
    }

    if (variant.sku) {
      item.sku = variant.sku;
    }

    /*
     * Pulisce l'ecommerce precedente.
     */
    window.dataLayer.push({
      ecommerce: null
    });

    /*
     * Inserisce il nuovo evento.
     */
    window.dataLayer.push({
      event: EVENT_NAME,
      ecommerce: {
        currency: currency,
        value: price,
        items: [item]
      }
    });

    console.log(
      '[Custom DataLayer] Evento wishlist inviato:',
      {
        event: EVENT_NAME,
        ecommerce: {
          currency: currency,
          value: price,
          items: [item]
        }
      }
    );
  }

  /**
   * Intercetta il clic sul cuore Wishlist King.
   *
   * Usiamo pointerdown e capture=true per leggere
   * lo stato prima che l'app aggiorni il pulsante.
   */
  document.addEventListener(
    'pointerdown',
    async function (event) {
      const button =
        event.target.closest(BUTTON_SELECTOR);

      if (!button) {
        return;
      }

      const component =
        button.closest(COMPONENT_SELECTOR);

      if (!component) {
        return;
      }

      /*
       * Stato prima del clic:
       *
       * false = il prodotto sta per essere aggiunto;
       * true  = il prodotto sta per essere rimosso.
       */
      const wasAlreadySelected =
        button.classList.contains('wk-selected');

      /*
       * Non invia add_to_wishlist durante
       * la rimozione.
       */
      if (wasAlreadySelected) {
        console.log(
          '[Custom DataLayer] Rimozione wishlist ignorata.'
        );

        return;
      }

      const productHandle =
        component.dataset.productHandle;

      if (!productHandle) {
        console.warn(
          '[Custom DataLayer] data-product-handle non trovato.'
        );

        return;
      }

      const requestKey =
        productHandle +
        ':' +
        (component.dataset.variantId || '');

      if (requestsInProgress.has(requestKey)) {
        return;
      }

      requestsInProgress.add(requestKey);

      try {
        /*
         * Attende la conferma dell'aggiunta.
         */
        const successfullyAdded =
          await waitForWishlistSelection(component);

        if (!successfullyAdded) {
          console.warn(
            '[Custom DataLayer] Aggiunta non confermata:',
            productHandle
          );

          return;
        }

        const requestedVariantId =
          getVariantId(component);

        const product =
          await fetchProduct(productHandle);

        const variant =
          selectVariant(
            product,
            requestedVariantId
          );

        if (!variant) {
          throw new Error(
            'Nessuna variante trovata per ' +
            productHandle
          );
        }

        pushWishlistEvent(
          product,
          variant
        );
      } catch (error) {
        console.error(
          '[Custom DataLayer] Errore wishlist:',
          error
        );
      } finally {
        setTimeout(function () {
          requestsInProgress.delete(requestKey);
        }, 500);
      }
    },

    /*
     * Capture impostato a true.
     */
    true
  );
})();